import { templateEditorLogger } from '../../logger.instance';
import type { Literal } from '../ast-node.model';

const logger = templateEditorLogger.extend('literal-parser');

/**
 * Utility for parsing and creating literal values from strings.
 */
export class LiteralParser {
  /**
   * Creates a Literal node from a string representation.
   */
  public static parse(original: string): Literal {
    logger.debug('Parsing literal', { original });
    const num = Number.parseFloat(original);
    if (!Number.isNaN(num) && Number.isFinite(num) && String(num) === original) {
      return {
        type: 'NumberLiteral',
        value: num,
        original,
        toString: () => original,
      };
    }

    if (original === 'true') {
      return {
        type: 'BooleanLiteral',
        value: true,
        original,
        toString: () => original,
      };
    }

    if (original === 'false') {
      return {
        type: 'BooleanLiteral',
        value: false,
        original,
        toString: () => original,
      };
    }

    if (original === 'null') {
      return {
        type: 'NullLiteral',
        value: null,
        original,
        toString: () => original,
      };
    }

    if (original === 'undefined') {
      return {
        type: 'UndefinedLiteral',
        value: undefined,
        original,
        toString: () => original,
      };
    }

    return {
      type: 'StringLiteral',
      value: original.replaceAll(/^["']|["']$/g, ''),
      original,
      toString: () => `"${original.replaceAll(/^["']|["']$/g, '')}"`,
    };
  }

  /**
   * Checks if a string represents a literal value.
   */
  public static isLiteral(str: string): boolean {
    const isQuoted =
      (str.startsWith('"') && str.endsWith('"')) || (str.startsWith("'") && str.endsWith("'"));
    const isNumeric = !Number.isNaN(Number(str)) && Number.isFinite(Number(str));
    const isKeyword = ['true', 'false', 'null', 'undefined'].includes(str);

    return isQuoted || isNumeric || isKeyword;
  }
}
